import React, { useEffect } from 'react';
import { cstList } from '../../ProData';
import { Helmet } from "react-helmet-async";
import { GraduationCap, BookOpen, Award, CheckCircle, Clock, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function MThContent() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="py-5" style={{ background: '#f8fafc', minHeight: '85vh' }}>
      <Helmet>
        <title>Master of Theology (M.Th. / S.T.L.) - Vidyajyoti College of Theology</title>
        <meta name="description" content="Master of Theology (M.Th.) and Licentiate in Sacred Theology (S.T.L.) in Contextual Systematic Theology at Vidyajyoti College, Delhi." />
        <meta name="keywords" content="MTh Theology, STL Theology, Licentiate in Sacred Theology, Contextual Theology India, Vidyajyoti master program" />
        <link rel="canonical" href="/academics/master" />
      </Helmet>

      <div className="container">
        {/* Header Hero Card */}
        <div className="card border-0 rounded-4 shadow-sm p-4 p-md-5 mb-5" style={{ background: 'linear-gradient(135deg, #1a2b56 0%, #2a3f75 100%)', color: '#fff' }}>
          <div className="row align-items-center">
            <div className="col-lg-8">
              <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-3" style={{ background: 'rgba(198, 146, 20, 0.2)', color: '#ffd56b', fontSize: '0.85rem', fontWeight: 600, border: '1px solid rgba(198, 146, 20, 0.4)' }}>
                <Sparkles size={14} /> Second Cycle (Postgraduate)
              </div>
              <h1 className="display-6 fw-bold mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Master of Theology (M.Th. / S.T.L.)
              </h1>
              <h5 className="text-light fw-normal mb-3" style={{ opacity: 0.9 }}>
                Licentiate in Sacred Theology (S.T.L.) in Contextual Systematic Theology
              </h5>
              <p className="lead mb-0 text-light" style={{ fontSize: '1rem', lineHeight: '1.7', opacity: 0.85 }}>
                The S.T.L Programme deals organically with specialized theological disciplines so that the understanding of divine revelation and Indian religious traditions is deepened and scientific methods of scholarly research are mastered.
              </p>
            </div>
            <div className="col-lg-4 text-center mt-4 mt-lg-0">
              <div className="p-4 rounded-4" style={{ background: 'rgba(255, 255, 255, 0.08)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.15)' }}>
                <GraduationCap size={44} className="mb-2" style={{ color: '#ffd56b' }} />
                <h6 className="fw-bold mb-1 text-white">4 Semesters (2 Years)</h6>
                <p className="small mb-0 text-light opacity-75">Full-Time Residential Research</p>
                <hr style={{ borderColor: 'rgba(255,255,255,0.2)' }} />
                <Link to="/admission/details" className="btn btn-sm w-100 rounded-pill fw-semibold text-white" style={{ background: '#79031d' }}>
                  Admission Details
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Program Pillars */}
        <div className="row g-4 mb-5">
          <div className="col-md-4">
            <div className="card h-100 border-0 rounded-4 shadow-sm p-4 bg-white" style={{ borderLeft: '4px solid #1a2b56' }}>
              <div className="d-inline-flex p-3 rounded-circle mb-3" style={{ background: 'rgba(26, 43, 86, 0.08)', color: '#1a2b56' }}>
                <BookOpen size={24} />
              </div>
              <h5 className="fw-bold mb-2" style={{ color: '#1a2b56' }}>Contextual Focus</h5>
              <p className="text-muted small mb-0" style={{ lineHeight: '1.6' }}>
                Focuses on Indian Christian Theology, subaltern hermeneutics, socio-cultural realities, and inter-faith dialogue across South Asia.
              </p>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card h-100 border-0 rounded-4 shadow-sm p-4 bg-white" style={{ borderLeft: '4px solid #c69214' }}>
              <div className="d-inline-flex p-3 rounded-circle mb-3" style={{ background: 'rgba(198, 146, 20, 0.12)', color: '#c69214' }}>
                <Award size={24} />
              </div>
              <h5 className="fw-bold mb-2" style={{ color: '#1a2b56' }}>Ecclesiastical Degrees</h5>
              <p className="text-muted small mb-0" style={{ lineHeight: '1.6' }}>
                Grants the Pontifical Licentiate in Sacred Theology (S.T.L.) recognized worldwide for seminary teaching and advanced research.
              </p>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card h-100 border-0 rounded-4 shadow-sm p-4 bg-white" style={{ borderLeft: '4px solid #79031d' }}>
              <div className="d-inline-flex p-3 rounded-circle mb-3" style={{ background: 'rgba(121, 3, 29, 0.08)', color: '#79031d' }}>
                <Clock size={24} />
              </div>
              <h5 className="fw-bold mb-2" style={{ color: '#1a2b56' }}>Rigorous Research</h5>
              <p className="text-muted small mb-0" style={{ lineHeight: '1.6' }}>
                Demands two comprehensive seminar research papers along with a defended scientific Master’s dissertation.
              </p>
            </div>
          </div>
        </div>

        {/* Curriculum / Subject Matrix */}
        <div className="card border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white mb-5">
          <div className="d-flex flex-wrap align-items-center justify-content-between mb-4 pb-2 border-bottom">
            <div>
              <span className="badge px-2.5 py-1 rounded-pill mb-2" style={{ background: '#79031d', color: '#fff' }}>Syllabus Matrix</span>
              <h3 className="fw-bold mb-0" style={{ color: '#1a2b56' }}>Contextual Systematic Theology Courses</h3>
            </div>
            <span className="text-muted small">Updated Academic Calendar</span>
          </div>

          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead style={{ background: 'rgba(26, 43, 86, 0.05)', color: '#1a2b56' }}>
                <tr>
                  <th className="py-3 px-3">#</th>
                  <th className="py-3">Date / Term</th>
                  <th className="py-3">Subject / Seminar Theme</th>
                  <th className="py-3">Presenter / Faculty</th>
                  <th className="py-3">Academic Year</th>
                </tr>
              </thead>
              <tbody>
                {cstList && cstList.map((item, index) => (
                  <tr key={index}>
                    <td className="px-3 fw-bold text-muted">{index + 1}</td>
                    <td>
                      <span className="badge rounded-pill bg-light text-dark border px-2.5 py-1">
                        {item.date || 'Regular Session'}
                      </span>
                    </td>
                    <td className="fw-semibold" style={{ color: '#1a2b56' }}>
                      {item.subjects}
                    </td>
                    <td className="text-muted small">
                      {item.by || 'Faculty of Theology'}
                    </td>
                    <td>
                      <span className="badge px-2 py-1 rounded-pill" style={{ background: 'rgba(198, 146, 20, 0.15)', color: '#8a6200', fontWeight: 600 }}>
                        {item.year || '2024-2026'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Examination & Evaluation Guidelines */}
        <div className="row g-4">
          <div className="col-lg-6">
            <div className="card h-100 border-0 rounded-4 shadow-sm p-4 bg-white">
              <h4 className="fw-bold mb-3" style={{ color: '#1a2b56' }}>Examination & Defense</h4>
              <p className="text-muted" style={{ lineHeight: '1.7', textAlign: 'justify' }}>
                The S.T.L comprehensive examination consists of a 90-minute oral defense before a board of three specialized examiners, covering the core curriculum, the two major research papers, and the final Dissertation.
              </p>
              <ul className="list-unstyled mb-0">
                <li className="d-flex align-items-start gap-2 mb-2">
                  <CheckCircle size={18} className="text-success flex-shrink-0 mt-1" />
                  <span className="small text-muted">Comprehensive evaluation carries a weightage of 30% of total score.</span>
                </li>
                <li className="d-flex align-items-start gap-2 mb-2">
                  <CheckCircle size={18} className="text-success flex-shrink-0 mt-1" />
                  <span className="small text-muted">Scientific theological methodology demonstrated through peer-reviewed research papers.</span>
                </li>
                <li className="d-flex align-items-start gap-2">
                  <CheckCircle size={18} className="text-success flex-shrink-0 mt-1" />
                  <span className="small text-muted">Guided dissertation under an appointed faculty mentor and thesis committee.</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="card h-100 border-0 rounded-4 shadow-sm p-4" style={{ background: 'linear-gradient(135deg, rgba(121,3,29,0.04) 0%, rgba(26,43,86,0.06) 100%)', border: '1px solid rgba(121,3,29,0.1)' }}>
              <h4 className="fw-bold mb-3" style={{ color: '#79031d' }}>Admission Requirements</h4>
              <p className="text-muted small mb-3" style={{ lineHeight: '1.7' }}>
                Candidates seeking admission to the S.T.L. must hold a recognized Bachelor of Theology (B.Th.) degree from Vidyajyoti or another ecclesiastical faculty with a minimum aggregate of 55% (High Second Class).
              </p>
              <div className="p-3 rounded-3 bg-white shadow-sm border mb-3">
                <div className="d-flex align-items-center justify-content-between">
                  <span className="small fw-semibold text-dark">Have questions about admissions?</span>
                  <Link to="/contact" className="btn btn-sm rounded-pill text-white" style={{ background: '#1a2b56' }}>
                    Contact Registrar
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
